<template>
  <div class="zoo-content">
    <div class="zoo-content-header">
      <h3>{{ t('zooContent.title') }}</h3>
      <AeButton variant="primary" small @click="goToNewHerd">{{ t('zooContent.newHerd') }}</AeButton>
    </div>

    <div v-if="loading" class="zoo-loading">{{ t('zooContent.loading') }}</div>

    <div v-else-if="herds.length === 0" class="zoo-empty">
      <p class="zoo-empty-title">{{ t('zooContent.emptyTitle') }}</p>
      <p class="zoo-empty-desc">{{ t('zooContent.emptyDescription') }}</p>
      <AeButton variant="primary" @click="goToNewHerd">{{ t('zooContent.createFirstHerd') }}</AeButton>
    </div>

    <div v-else class="herd-grid">
      <div
        v-for="herd in herds"
        :key="herd.id_herd"
        class="herd-card"
        @click="goToHerd(herd)"
      >
        <div class="herd-card-header">
          <span class="specie-badge">{{ t(herd.specie) }}</span>
        </div>
        <div class="herd-name">{{ herd.name }}</div>
        <div v-if="herd.herd_code" class="herd-code">{{ herd.herd_code }}</div>
        <div class="herd-stats">
          <span class="stat-count">{{ t('zooContent.capiCount', { count: herd._total ?? '…' }) }}</span>
          <span class="stat-groups">{{ t('zooContent.gruppiCount', { count: herd._groups ?? '…' }) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useZooStore } from '../zooStore.js'
import { AeButton } from '@aevue/components'

const props = defineProps({
  farmCenterId: { type: String, required: true }
})

const { t } = useI18n()
const router   = useRouter()
const zooStore = useZooStore()

const loading = ref(true)
const herds   = ref([])

async function load() {
  loading.value = true
  try {
    const raw = await zooStore.getHerds(props.farmCenterId)
    for (const herd of raw) {
      const stats = await zooStore.getHerdStatistics(herd.id_herd)
      herd._total = stats.total
      const groups = await zooStore.getAnimalGroups(herd.id_herd)
      herd._groups = groups.length
    }
    herds.value = raw
  } finally {
    loading.value = false
  }
}

function goToHerd(herd) {
  router.push({ name: 'HerdHome', params: { farmCenterId: props.farmCenterId, id_herd: herd.id_herd } })
}

function goToNewHerd() {
  router.push({ name: 'HerdCreate', params: { farmCenterId: props.farmCenterId } })
}

onMounted(load)
</script>

<style scoped>
.zoo-content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.zoo-content-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.zoo-loading {
  padding: 2rem;
  text-align: center;
  color: var(--ae-text-secondary, #888);
}

.zoo-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 2rem;
  text-align: center;
}

.zoo-empty-title {
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0;
}

.zoo-empty-desc {
  color: var(--ae-text-secondary, #888);
  font-size: 0.9rem;
  margin: 0;
}

.herd-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.75rem;
}

.herd-card {
  padding: 1rem;
  border-radius: 10px;
  background: var(--ae-surface, rgba(255,255,255,0.05));
  border: 1px solid var(--ae-border, rgba(0,0,0,0.08));
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.herd-card:hover {
  border-color: var(--ae-primary, #4a90d9);
}

.herd-card-header {
  margin-bottom: 0.15rem;
}

.specie-badge {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(74, 144, 217, 0.15);
  color: var(--ae-primary, #4a90d9);
}

.herd-name {
  font-size: 1rem;
  font-weight: 700;
}

.herd-code {
  font-size: 0.78rem;
  color: var(--ae-text-secondary, #888);
  font-family: monospace;
}

.herd-stats {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.25rem;
  font-size: 0.8rem;
  color: var(--ae-text-secondary, #aaa);
}

.stat-count { font-weight: 600; }
</style>
